/**
 * Permisos del gerente — qué módulos puede ver el rol `manager`, por organización.
 *
 * El dueño los enciende/apaga desde Ajustes (tarjeta "Permisos del gerente").
 * Cada apagado se guarda en business_settings como `manager_access_<id> = 'false'`;
 * la AUSENCIA de la clave significa visible, así que al desplegar esto ninguna
 * organización cambia de comportamiento hasta que su dueño apague algo.
 *
 * Dos consumidores, UN predicado: el menú (utils/navigation.js) y la guardia de
 * ruta (AppRoot). Si filtraran con criterios propios, el menú y la URL escrita a
 * mano podrían estar en desacuerdo.
 *
 * ⚠️ Es un control de APLICACIÓN, no de datos: la RLS de la organización sigue
 * dejando leer por API lo que la pantalla oculta. Misma advertencia que ya hace
 * navigation.js ("el modo no es un permiso").
 *
 * `id` debe coincidir con el `id` del item en utils/navigation.js.
 */

export const MANAGER_TOGGLABLE_MODULES = Object.freeze([
    { id: 'reports',    label: 'Reportes',             hint: 'Ventas históricas, gráficas y exportes' },
    { id: 'historial',  label: 'Historial de cambios', hint: 'Bitácora de ediciones y cancelaciones' },
    { id: 'employees',  label: 'Empleados y nómina',   hint: 'Fichas, tarifas y modelos de pago de todos' },
    { id: 'expenses',   label: 'Gastos',               hint: 'Registro de gastos del negocio' },
    { id: 'customers',  label: 'Clientes',             hint: 'Listado y fichas de clientes' },
    { id: 'services',   label: 'Servicios',            hint: 'Catálogo, precios y comisiones' },
    { id: 'promotions', label: 'Promociones',          hint: 'Campañas y envíos a clientes' },
    { id: 'memberships', label: 'Membresías',          hint: 'Configuración de planes de membresía' },
    { id: 'reservations', label: 'Reservas',           hint: 'Agenda y citas de clientes' },
    { id: 'materials',  label: 'Tienda de Materiales', hint: 'Pedidos de materiales al distribuidor' },
    { id: 'billing',    label: 'Facturación del sistema', hint: 'Plan y pagos de CarWash Pro' },
    { id: 'settings',   label: 'Ajustes del negocio',  hint: 'La pantalla de configuración' },
]);

export const managerAccessKey = (moduleId) => `manager_access_${moduleId}`;

// Solo los módulos de la lista pueden ocultarse. Sin este cerrojo, una clave
// espuria escrita por API (la RLS de business_settings deja escribir a
// cualquier miembro de la org) podría vaciarle el menú al gerente — Inicio y
// Dinero incluidos — sin que exista guardia de ruta equivalente.
const TOGGLABLE_IDS = new Set(MANAGER_TOGGLABLE_MODULES.map((m) => m.id));

/**
 * ¿Este módulo está oculto para el usuario actual?
 *
 * Solo muerde con rol `manager` exacto: al dueño (owner/admin/superadmin) nunca
 * le aplica, y `employee` ya no ve estos módulos por allowedRoles.
 *
 * Fail-open a propósito: mientras los settings cargan (objeto vacío) o si la
 * clave no existe, se muestra. Es un control de UX — un módulo legítimo
 * parpadeando bloqueado sería peor que uno oculto tardando un instante.
 */
export const isModuleHiddenForManager = (role, brandingSettings, moduleId) =>
    role === 'manager' &&
    TOGGLABLE_IDS.has(moduleId) &&
    brandingSettings?.[managerAccessKey(moduleId)] === 'false';
